import { AmcAccess } from '../types/user';
import { baseApi } from './baseApi';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  // Present on normal login and also when MFA enrollment is required (per backend sample)
  jwtToken?: string;
  email?: string;
  name?: string;
  role?: string;
  userId?: string;
  amcId?: string;
  amcName?: string;
  amcIds?: string[];
  lastloggedIn?: string;

  // 2FA fields
  mfaEnforcement?: 'expired' | 'grace' | 'required'; // when true, show setup prompt
  mfaEnrollmentRequired?: boolean; // when true, show setup prompt
  mfaType?: string; // e.g. 'totp'
  twoFASetupPromptedAt?: string;
  preAuthToken?: string; // used for /auth/login/2fa
  requires2FA?: boolean; // when true, go to 2FA login step
  // passkey/TOTP method metadata
  availableMfaMethods?: Array<'totp' | 'passkey'>;
  enrollableMfaMethods?: Array<'totp' | 'passkey'>;
  preferredMfaMethod?: 'totp' | 'passkey' | null;
  amcAccess?: AmcAccess[];
}

interface MicrosoftLoginRequest {
  idToken: string;
}

interface Login2FARequest {
  preAuthToken: string;
  token: string; // TOTP code
  rememberMethod?: boolean;
}

interface MfaSetupResponse {
  otpauthUrl?: string;
  secret?: string;
  setupToken: string;
}

interface MfaSetupRequest {
  preAuthToken?: string; // optional if Authorization header is provided
}

interface MfaVerifyRequest {
  setupToken: string;
  token: string; // TOTP code to verify enrollment
  preAuthToken?: string; // optional if Authorization header has JWT or preAuthToken
  rememberMethod?: boolean;
}

// WebAuthn types (keep broad to avoid coupling to specific libs)
interface WebAuthnOptionsRequest {
  preAuthToken?: string;
}

type PublicKeyCredentialCreationOptionsJSON = any;
type PublicKeyCredentialRequestOptionsJSON = any;

interface WebAuthnRegisterOptionsResponse extends PublicKeyCredentialCreationOptionsJSON { }
interface WebAuthnAuthOptionsResponse extends PublicKeyCredentialRequestOptionsJSON { }

interface WebAuthnRegisterVerifyRequest {
  preAuthToken?: string;
  attestationResponse: any; // serialized credential
  rememberMethod?: boolean;
}

interface WebAuthnAuthVerifyRequest {
  preAuthToken?: string;
  assertionResponse: any; // serialized assertion
  rememberMethod?: boolean;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    loginMicrosoft: builder.mutation<LoginResponse, MicrosoftLoginRequest>({
      query: (body) => ({
        url: '/auth/microsoft',
        method: 'POST',
        body,
      }),
    }),
    login2fa: builder.mutation<LoginResponse, Login2FARequest>({
      query: (body) => ({
        url: '/auth/login/2fa',
        method: 'POST',
        body,
      }),
    }),
    mfaSetup: builder.mutation<MfaSetupResponse, MfaSetupRequest | void>({
      query: (body) => ({
        url: '/auth/2fa/setup',
        method: 'POST',
        body,
      }),
    }),
    mfaVerify: builder.mutation<LoginResponse, MfaVerifyRequest>({
      query: (body) => ({
        url: '/auth/2fa/verify',
        method: 'POST',
        body,
      }),
    }),
    webauthnRegisterOptions: builder.mutation<WebAuthnRegisterOptionsResponse, WebAuthnOptionsRequest>({
      query: (body) => ({
        url: '/auth/webauthn/register/options',
        method: 'POST',
        body,
      }),
    }),
    webauthnRegisterVerify: builder.mutation<LoginResponse, WebAuthnRegisterVerifyRequest>({
      query: (body) => ({
        url: '/auth/webauthn/register/verify',
        method: 'POST',
        body,
      }),
    }),
    webauthnAuthOptions: builder.mutation<WebAuthnAuthOptionsResponse, WebAuthnOptionsRequest>({
      query: (body) => ({
        url: '/auth/webauthn/auth/options',
        method: 'POST',
        body,
      }),
    }),
    webauthnAuthVerify: builder.mutation<LoginResponse, WebAuthnAuthVerifyRequest>({
      query: (body) => ({
        url: '/auth/webauthn/auth/verify',
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    forgotPassword: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<void, { token: string; newPassword: string }>({
      query: (body) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body,
      }),
    }),
    getProfile: builder.query<LoginResponse, void>({
      query: () => '/auth/me',
      providesTags: ['User'],
    }),
    updateProfile: builder.mutation<LoginResponse, { name?: string; amcAccess?: AmcAccess[] }>({
      query: (body) => ({
        url: '/auth/me/profile',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    changePassword: builder.mutation<void, { currentPassword: string; newPassword: string }>({
      query: (body) => ({
        url: '/auth/me/password',
        method: 'PATCH',
        body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginMicrosoftMutation,
  useLogin2faMutation,
  useMfaSetupMutation,
  useMfaVerifyMutation,
  useWebauthnRegisterOptionsMutation,
  useWebauthnRegisterVerifyMutation,
  useWebauthnAuthOptionsMutation,
  useWebauthnAuthVerifyMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useGetProfileQuery,
} = authApi;
